
import React from 'react';

/**
 * Shared component for displaying a list of permits (Werkplannen).
 * Used in AdminDashboard and ApplicantDashboard.
 */
export default function PermitList({
    permits = [],
    loading = false,
    selectedPermit,
    onSelect,
    showApplicant = false,
    emptyMessage = "Geen werkplannen gevonden."
}) {

    const getStatusBadge = (status) => {
        switch (status) {
            case 'approved':
                return <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs font-bold border border-green-500/50">Goedgekeurd</span>;
            case 'completed':
                return <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs font-bold border border-blue-500/50">⚙️ Uitgevoerd / Te Keuren</span>;
            case 'rejected':
                return <span className="bg-red-500/20 text-red-400 px-2 py-1 rounded text-xs font-bold border border-red-500/50">Afgekeurd</span>;
            case 'active':
                return <span className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-xs font-bold border border-blue-500/50">IN UITVOERING</span>;
            case 'pending':
                return <span className="bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded text-xs font-bold border border-yellow-500/50">IN BEHANDELING</span>;
            default:
                return <span className="bg-slate-700 text-slate-400 px-2 py-1 rounded text-xs font-bold border border-slate-600">{status}</span>;
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '...';
        return new Date(dateStr).toLocaleString('nl-NL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return <div className="text-center py-8 text-slate-500">Laden...</div>;
    }

    if (!permits || permits.length === 0) {
        return <div className="text-center py-8 text-slate-500">{emptyMessage}</div>;
    }

    return (
        <div className="space-y-2">
            {permits.map(permit => {
                const isSelected = selectedPermit?.id === permit.id;

                return (
                    <div
                        key={permit.id}
                        onClick={(e) => {
                            if (onSelect) {
                                onSelect(permit);
                                // Optional: scroll logic could be handled by parent or here if passed a ref
                                if (!isSelected && e.currentTarget) {
                                    e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                }
                            }
                        }}
                        style={{ cursor: 'pointer' }}
                        className={`p-4 rounded-lg border transition-all relative group ${isSelected
                            ? 'bg-blue-600 border-blue-400 shadow-lg scale-[1.02] z-10'
                            : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800 hover:border-slate-500'
                            }`}
                    >
                        <div className="flex justify-between items-start mb-2">
                            <span
                                className="font-bold"
                                style={{ color: isSelected ? '#ffffff' : 'var(--color-text)' }}
                            >
                                {permit.ultimoNr || 'Geen Ultimo'}
                            </span>
                            {getStatusBadge(permit.status)}
                        </div>

                        <div
                            className="text-sm truncate mb-1"
                            style={{ color: isSelected ? '#ffffff' : 'var(--color-text-muted)' }}
                        >
                            {permit.description}
                        </div>

                        <div
                            className="text-xs mb-1"
                            style={{ color: isSelected ? '#e0f2fe' : 'var(--color-text-muted)' }}
                        >
                            {permit.location || 'Locatie onbekend'}
                        </div>

                        <div className="flex justify-between items-end">
                            <div
                                className="text-xs"
                                style={{ color: isSelected ? '#bfdbfe' : 'var(--color-text-muted)' }}
                            >
                                📅 {formatDate(permit.createdAt)}
                            </div>

                            {showApplicant && (
                                <div
                                    className="text-xs italic"
                                    style={{ color: isSelected ? '#bfdbfe' : '#64748b' }}
                                >
                                    👤 {permit.applicantName || 'Onbekend'}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
